import classNames from "classnames";
import { FormFeedback } from "reactstrap";
import styles from "shared/components/newFormInputs/FormSelect.module.scss";
import { IReactSelectProps, ISelectOption, ReactSelect } from "shared/components/ReactSelect";
import { FieldPath, FieldValues, useController, UseControllerProps, useFormContext } from "react-hook-form";
import { TranslatedUseControllerOptions, useTranslatedValidations } from "shared/validations/useTranslatedValidations";

type Props<TFieldValues extends FieldValues = FieldValues, TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>> =
    Omit<IReactSelectProps, "inputId" | "value">
    & Omit<UseControllerProps<TFieldValues, TFieldName>, "rules" | "defaultValue" | "disabled">
    & {
        rules?: TranslatedUseControllerOptions<TFieldValues, TFieldName>;
        onSelected?: (value: ISelectOption["value"]) => void;
        testId?: string;
    };

export const FormSelect = <TFieldValues extends FieldValues, TFieldName extends FieldPath<TFieldValues>>({
    className,
    isClearable,
    isDisabled,
    isLoading,
    placeholder,
    noOptionsMessage,
    onSelected,
    name,
    rules,
    shouldUnregister,
    control,
    testId,
    ...rest
}: Props<TFieldValues, TFieldName>) => {
    const { resetField, formState: { isSubmitted } } = useFormContext();

    const translatedRules = useTranslatedValidations<TFieldValues, TFieldName>(rules);
    const { field, fieldState: { isTouched, invalid, error } } = useController<TFieldValues, TFieldName>({
        name,
        rules: translatedRules,
        shouldUnregister,
        control,
        disabled: isDisabled,
    });

    return <div
        className={classNames(styles.formSelect, (isTouched || isSubmitted) && ["was-validated", invalid ? "invalid" : "valid"], "form-select")}
        data-cy={className}
        data-testid={testId}>
        <ReactSelect
            inputId={field.name}
            isClearable={isClearable}
            isDisabled={isLoading || isDisabled || false}
            isLoading={isLoading || false}
            value={field.value ? field.value : undefined}
            onBlur={field.onBlur}
            onSelected={onOptionSelected}
            placeholder={placeholder}
            className={className}
            noOptionsMessage={noOptionsMessage}
            innerRef={field.ref}
            name={field.name}
            aria-invalid={invalid}
            aria-errormessage={invalid ? `${field.name}Errors` : undefined}
            {...rest}
        />
        <FormFeedback id={`${field.name}Errors`} className={invalid ? "d-block" : "d-none"} role="alert">{error?.message}</FormFeedback>
    </div>;

    function onOptionSelected(option: ISelectOption | null) {
        if (!!option) {
            field.onChange(option.value);
            onSelected?.(option.value);
        } else {
            // TODO: Fix this not allowing a value to be cleared when isClearable=true (SB-12253)
            resetField(name, { keepTouched: true });
        }
    }
};
